import React, { useRef, useState } from 'react';

// Define TooltipProps interface for chart tooltips
interface TooltipProps<TValue, TName> {
  active?: boolean;
  payload?: Array<{
    name?: TName;
    value?: TValue;
    color?: string;
  }>;
  label?: string;
}
// TODO: Replace with ECharts line chart implementation
// import {
//   LineChart as RechartsLineChart,
//   Line,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
//   TooltipProps,
//   Brush,
//   ReferenceLine
// } from 'recharts';
import { styled, Box, Typography, useTheme, IconButton, Tooltip as MuiTooltip, Menu, MenuItem } from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import { exportChartToPng, exportChartDataToCsv, handleChartExportToPdf } from '../../utils/chartUtils';

export interface LineChartDataItem {
  name: string;
  [key: string]: string | number;
}

export interface LineSeries {
  dataKey: string;
  name?: string;
  color?: string;
  dashArray?: string;
  type?: 'monotone' | 'linear' | 'step' | 'stepBefore' | 'stepAfter' | 'natural' | 'basis' | 'basisOpen' | 'basisClosed';
  strokeWidth?: number;
}

export interface LineChartProps {
  /** Chart data */
  data: LineChartDataItem[];
  /** Data series to display */
  series: LineSeries[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Show chart grid */
  grid?: boolean;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Margin around chart */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Enable chart interactions (zoom, pan, export) */
  interactive?: boolean;
  /** Show brush for time range selection */
  showBrush?: boolean;
  /** Reference line value (e.g., for thresholds) */
  referenceLine?: { y?: number; x?: string | number; label?: string; color?: string };
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Function to refresh data */
  onRefresh?: () => void;
  /** Refresh interval in ms */
  refreshInterval?: number;
}

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  position: 'relative',
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

const RefreshIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(2),
}));

const CustomTooltip: React.FC<TooltipProps<number | string, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: 1,
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography
            key={`tooltip-${index}`}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  }

  return null;
};

export const LineChart: React.FC<LineChartProps> = ({
  data,
  series,
  title,
  height = 400,
  xAxisLabel,
  yAxisLabel,
  grid = true,
  tooltipFormatter,
  margin = { top: 10, right: 30, left: 0, bottom: 0 },
  interactive = true,
  showBrush = false,
  referenceLine,
  isRefreshing = false,
  onRefresh,
  refreshInterval,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Set initial data load timestamp
  React.useEffect(() => {
    if (data.length > 0 && !lastUpdated) {
      setLastUpdated(new Date());
    }
  }, [data]);
  
  const chartMargin = {
    top: margin.top || 10,
    right: margin.right || 30,
    bottom: showBrush ? 60 : (margin.bottom || 0),
    left: margin.left || 0,
  };
  
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    exportChartToPng(chartRef, title || 'line-chart');
  };
  
  const handleExportCsv = () => {
    handleExportMenuClose();
    exportChartDataToCsv(data, title || 'line-chart-data');
  };
  
  const handleExportPdf = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'line-chart', title);
  };
  
  const handleExportPdfWithData = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'line-chart', title, true);
  };
  
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  };

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';

  return (
    <ChartContainer>
      <ChartHeader>
        {title && <ChartTitle variant="h6">{title}</ChartTitle>}
        <ControlsContainer>
          {lastUpdated && (
            <RefreshIndicator>
              {formattedLastUpdated}
            </RefreshIndicator>
          )}
          
          {onRefresh && (
            <MuiTooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
          
          {interactive && (
            <>
              <MuiTooltip title="Export options">
                <IconButton size="small" onClick={handleExportMenuOpen}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
                <MenuItem onClick={handleExportPdf}>Export as PDF</MenuItem>
                <MenuItem onClick={handleExportPdfWithData}>Export as PDF with data table</MenuItem>
                <MenuItem onClick={handleExportCsv}>Export data as CSV</MenuItem>
              </Menu>
            </>
          )}
        </ControlsContainer>
      </ChartHeader>
      
      <ChartWrapper ref={chartRef}>
        {interactive ? (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit={true}
            wheel={{ step: 0.1 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    right: 10, 
                    top: 10, 
                    zIndex: 10, 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: 1,
                    padding: 0.5
                  }}
                >
                  <MuiTooltip title="Zoom in">
                    <IconButton onClick={() => zoomIn()} size="small">
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Zoom out">
                    <IconButton onClick={() => zoomOut()} size="small">
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Reset zoom">
                    <IconButton onClick={() => resetTransform()} size="small">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </Box>
                <TransformComponent wrapperStyle={{ width: '100%', height: height }}>
                  <div style={{ width: '100%', height: '100%' }}>
                    {/* TODO: Replace with ECharts line chart */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                          Line Chart Visualization
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interactive line chart coming soon
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Chart functionality temporarily disabled during migration to ECharts
                        </Typography>
                      </Box>
                    </Box>
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #ccc',
              borderRadius: 2,
              backgroundColor: '#f9f9f9'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Line Chart Visualization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Static line chart coming soon
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Chart functionality temporarily disabled during migration to ECharts
              </Typography>
            </Box>
          </Box>
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};

export default LineChart;
